import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { 
    FaFilePdf,
    FaArrowLeft
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const EngineerSubheWorkDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [userData, setUserData] = useState(null);
    const [works, setWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEngineer, setIsEngineer] = useState(true);

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);
        let found = false;

        const handleData = (snapshot, isEng) => {
            if (snapshot.exists() && !found) {
                found = true;
                setIsEngineer(isEng);
                const data = snapshot.val();
                setUserData({
                    ...data,
                    displayName: data.firstName ? `${data.firstName} ${data.surname}` : (data.societyName || data.name || "-")
                });

                if (data.GeneratedLottery) {
                    const extractedWorks = Object.entries(data.GeneratedLottery).map(([dateKey, workData]) => ({
                        dateKey,
                        ...workData,
                        localStatus: workData.status || "स्थिती",
                        localRemark: workData.remarks || ""
                    }));
                    setWorks(extractedWorks);
                }
                setLoading(false);
            }
        };

        const engRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
        const socRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);

        onValue(engRef, (s) => handleData(s, true));
        onValue(socRef, (s) => handleData(s, false));

        const timer = setTimeout(() => {
            if (!found) setLoading(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [id]);

    const handleUpdate = async (dateKey, workNumber) => {
        const workIdx = works.findIndex(w => w.dateKey === dateKey);
        if (workIdx === -1) return;

        const work = works[workIdx];
        const path = isEngineer 
            ? `ekamwatapzp/EngineerRegistrations/${id}/GeneratedLottery/${dateKey}`
            : `ekamwatapzp/SocietyRegistrations/${id}/GeneratedLottery/${dateKey}`;

        try {
            await update(ref(db, path), {
                status: work.localStatus,
                remarks: work.localRemark
            });
            toast.success("माहिती यशस्वीरित्या अपडेट झाली!");
        } catch (error) {
            console.error("Update error:", error);
            toast.error("अपडेट करताना त्रुटी आली.");
        }
    };

    const handleFieldChange = (idx, field, value) => {
        const updatedWorks = [...works];
        updatedWorks[idx][field] = value;
        setWorks(updatedWorks);
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </EngineerLayout>
        );
    }

    if (!userData) {
        return (
            <EngineerLayout>
                <div className="p-8 text-center text-gray-400 font-bold font-marathi">माहिती सापडली नाही.</div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header with Breadcrumbs */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-blue-500 transition active:scale-95"
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight">
                            अर्जदाराचे नाव :- {userData.displayName}
                        </h1>
                    </div>
                    <div className="text-[12px] font-black text-gray-400 uppercase tracking-widest bg-white px-6 py-3 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-2">
                        जिल्हा परिषद / <span className="text-gray-600">सुभे कामांची यादी पहा</span>
                    </div>
                </div>

                {/* Main Table Container */}
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-4 md:p-8 overflow-x-auto min-h-[400px]">
                    <table className="w-full border-collapse border border-gray-200">
                        <thead className="bg-white text-[13px] font-black text-gray-700 border-b border-gray-200">
                            <tr>
                                <th className="px-2 py-5 border border-gray-200 w-12 text-center">अ.क्र.</th>
                                <th className="px-4 py-5 border border-gray-200 min-w-[300px] text-left">कामाचे नांव</th>
                                <th className="px-3 py-5 border border-gray-200 w-24 text-center">लॉटरी क्रमांक</th>
                                <th className="px-3 py-5 border border-gray-200 w-28 text-center text-gray-700">कामाची किंमत</th>
                                <th className="px-4 py-5 border border-gray-200 min-w-[150px] text-left">विभागाचे नाव</th>
                                <th className="px-4 py-5 border border-gray-200 w-32 text-center">काम पूर्ण करणे</th>
                                <th className="px-4 py-5 border border-gray-200 w-44 text-center">काम पूर्ण करण्याचा दिनांक</th>
                                <th className="px-4 py-5 border border-gray-200 w-40 text-center">स्थिती निवडा</th>
                                <th className="px-4 py-5 border border-gray-200 min-w-[200px] text-left">शेरा</th>
                                <th className="px-4 py-5 border border-gray-200 w-32 text-center bg-gray-50/50">कृती</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {works.map((work, idx) => (
                                <tr key={idx} className="hover:bg-blue-50/10 transition-colors text-[13px] text-gray-800 font-bold">
                                    <td className="px-2 py-6 border border-gray-200 text-center text-gray-400">{toMarathiNumber(idx + 1)}</td>
                                    <td className="px-4 py-6 border border-gray-200 leading-relaxed group">
                                        <div className="flex flex-col gap-2">
                                            <span>{work.workName}</span>
                                            <button className="self-start text-red-500 hover:text-red-700 transition">
                                                <FaFilePdf size={22} className="p-0.5 border border-red-100 rounded" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-6 border border-gray-200 text-center">{toMarathiNumber(work.lotteryOrder || work.preference || "३")}</td>
                                    <td className="px-3 py-6 border border-gray-200 text-center text-gray-500 font-black">{toMarathiNumber(work.tenderAmount || work.amount || "४०००००")}</td>
                                    <td className="px-4 py-6 border border-gray-200">{work.taluka || "सा.बां. उत्तर विभाग"}</td>
                                    <td className="px-4 py-6 border border-gray-200 text-center">{work.workDuration || "६ महिने"}</td>
                                    <td className="px-4 py-6 border border-gray-200 text-center text-gray-500 italic">{work.completionDate || "२०२२-०२-१८"}</td>
                                    <td className="px-4 py-6 border border-gray-200 text-center">
                                        <select 
                                            value={work.localStatus}
                                            onChange={(e) => handleFieldChange(idx, 'localStatus', e.target.value)}
                                            className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 focus:ring-2 focus:ring-blue-100 outline-none transition font-bold text-gray-600 appearance-none"
                                        >
                                            <option value="स्थिती">स्थिती</option>
                                            <option value="In Progress">सुरू आहे</option>
                                            <option value="Completed">पूर्ण झाले</option>
                                        </select>
                                    </td>
                                    <td className="px-4 py-6 border border-gray-200">
                                        <input 
                                            type="text"
                                            value={work.localRemark}
                                            onChange={(e) => handleFieldChange(idx, 'localRemark', e.target.value)}
                                            placeholder="शेरा प्रविष्ट करा"
                                            className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none transition font-bold"
                                        />
                                    </td>
                                    <td className="px-4 py-6 border border-gray-200 text-center bg-gray-50/30">
                                        <button 
                                            onClick={() => handleUpdate(work.dateKey, work.workNumber)}
                                            className="bg-[#4e73df] hover:bg-blue-700 text-white px-6 py-2 rounded font-black text-[12px] shadow-lg shadow-blue-500/20 transition transform active:scale-90 w-full"
                                        >
                                            अपडेट करा
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {works.length === 0 && (
                        <div className="py-32 text-center text-gray-300 font-bold italic text-sm tracking-widest uppercase">
                            कोणतीही माहिती उपलब्ध नाही.
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                table { border-collapse: collapse !important; }
                th { background-color: white !important; }
                @media print {
                    .no-print { display: none !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheWorkDetail;
